/**
 * Field validation for the auth forms.
 *
 * Validators return i18n keys, never sentences — the component calls `t(key)`.
 * The repo ships neither react-hook-form nor zod, so these are plain
 * predicates; swap the bodies for a zod schema if those land later, the
 * `FieldErrors` return shape can stay.
 *
 * These mirror the server rules in
 * `backend/app/Http/Requests/v1/AuthRequests/*` — they exist to save a round
 * trip, not to replace it. The backend is still the authority, and its 422
 * field map reaches the forms as `ApiError.fieldError(name)`. Keep the two in
 * step when the requests change.
 */

export type FieldErrors<TField extends string> = Partial<
  Record<TField, string>
>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const OTP_RE = /^\d{6}$/;
const PASSWORD_MIN_LENGTH = 8;
const NAME_MIN_LENGTH = 3;

export function validateEmail(value: string): string | undefined {
  if (!value.trim()) return "auth.errors.emailRequired";
  if (!EMAIL_RE.test(value)) return "auth.errors.emailInvalid";
  return undefined;
}

export function validatePassword(value: string): string | undefined {
  if (!value) return "auth.errors.passwordRequired";
  if (value.length < PASSWORD_MIN_LENGTH) return "auth.errors.passwordMin";
  return undefined;
}

export function validateFirstName(value: string): string | undefined {
  if (!value.trim()) return "auth.errors.firstNameRequired";
  if (value.trim().length < NAME_MIN_LENGTH) return "auth.errors.firstNameMin";
  return undefined;
}

export function validateLastName(value: string): string | undefined {
  if (!value.trim()) return "auth.errors.lastNameRequired";
  if (value.trim().length < NAME_MIN_LENGTH) return "auth.errors.lastNameMin";
  return undefined;
}

/** The reset code is exactly six digits. */
export function validateOtp(value: string): string | undefined {
  const otp = value.trim();
  if (!otp) return "auth.errors.otpRequired";
  if (!OTP_RE.test(otp)) return "auth.errors.otpInvalid";
  return undefined;
}

export function validateConfirm(
  password: string,
  confirm: string
): string | undefined {
  if (!confirm) return "auth.errors.confirmRequired";
  if (password !== confirm) return "auth.errors.confirmMismatch";
  return undefined;
}
